import { useEffect, useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ExternalLink, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  Section,
  Select,
  Spinner,
  Switch,
  Textarea,
} from '@zt/ui';
import type { Tone } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import {
  EditorLanguages,
  LocalePicker,
  localeName,
  TranslateButton,
  TranslationLinks,
  useContentLocale,
} from '../components/ContentLocale';
import { ContentReviewPanel, reviewTone } from '../components/ContentReview';
import { ImageUpload } from '../components/ImageUpload';
import { ProductMultiPicker } from '../components/ProductMultiPicker';
import { RichTextEditor } from '../components/RichTextEditor';
import { api, ApiError } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import { PostTranslateDraft } from './ai/drafts';
import type { PostCategoryList, PostDetail, PostList, PreviewLink } from '../lib/types';
import { StorePicker, useStoreChoice } from './payments';
import { openInNewTab } from '../lib/open-tab';

const stateTone: Record<PostList[number]['state'], Tone> = {
  draft: 'neutral',
  scheduled: 'amber',
  live: 'moss',
  unpublished: 'slate',
};
type Filter = 'all' | 'review' | 'live' | 'draft';

// ---------------------------------------------------------------------------- Liste

/** Blog: yazılar ve kategoriler. */
export function BlogPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const { stores, loading, storeId, setStoreId } = useStoreChoice();
  const { locales, locale, setLocale } = useContentLocale(storeId);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');
  const list = useQuery({
    queryKey: ['posts', storeId, locale],
    queryFn: () => api.get<PostList>(`/v1/admin/posts?storeId=${storeId}&locale=${locale}`),
    enabled: !!storeId && !!locale,
  });
  if (loading) return <Spinner label={t('common.loading')} />;
  const items = (list.data ?? []).filter((p) =>
    filter === 'review'
      ? p.reviewStatus === 'pending'
      : filter === 'live'
        ? p.state === 'live' || p.state === 'scheduled'
        : filter === 'draft'
          ? p.state === 'draft' || p.state === 'unpublished'
          : true,
  );
  const pending = (list.data ?? []).filter((p) => p.reviewStatus === 'pending').length;
  return (
    <PageFrame
      helpKey="page.blog"
      actions={
        can('content.write') &&
        storeId && (
          <div className="flex items-center gap-2">
            <Button variant="primary" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" aria-hidden />
              {t('admin.blog.create')}
            </Button>
            <HelpTip k="blog.create" />
          </div>
        )
      }
    >
      {!stores.length ? (
        <EmptyState title={t('admin.payments.noStore')} description={t('admin.payments.noStoreLead')} />
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-4">
            <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
            <LocalePicker locales={locales} locale={locale} onChange={setLocale} />
          </div>
          <Section title={t('admin.blog.posts')} className="border-t-0 pt-0">
            <div className="mb-3 flex flex-wrap items-end gap-3">
              <Field label={t('admin.blog.filter')} help={<HelpTip k="blog.filter" />} className="w-56">
                {(p) => (
                  <Select {...p} value={filter} onChange={(e) => setFilter(e.target.value as Filter)}>
                    {(['all', 'review', 'live', 'draft'] as const).map((f) => (
                      <option key={f} value={f}>
                        {t(`admin.blog.filters.${f}`)}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
              {pending > 0 && <Badge tone="amber">{t('admin.blog.pendingCount', { count: pending })}</Badge>}
            </div>
            {!list.data ? (
              <Spinner label={t('common.loading')} />
            ) : !items.length ? (
              <EmptyState title={t('admin.blog.empty')} description={t('admin.blog.emptyLead')} />
            ) : (
              <ul className="divide-y divide-line rounded-md border border-line bg-surface">
                {items.map((p) => (
                  <li key={p.id} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 text-sm">
                    <div className="min-w-0 flex-1">
                      <Link
                        to="/blog/$id"
                        params={{ id: p.id }}
                        className="font-medium text-pine hover:underline"
                      >
                        {p.title}
                      </Link>
                      <p className="text-ink-3">
                        /blog/{p.slug}
                        {p.category ? ` · ${p.category}` : ''} ·{' '}
                        {p.firstPublishedAt
                          ? t('admin.blog.publishedInfo', { date: formatDate(p.firstPublishedAt, true) })
                          : t('admin.pages.updated', { date: formatDate(p.updatedAt, true) })}
                      </p>
                      <TranslationLinks
                        items={p.translations}
                        to="/blog/$id"
                        stateLabel={(s) => t(`admin.blog.states.${s}`)}
                      />
                    </div>
                    <Badge tone={stateTone[p.state]}>{t(`admin.blog.states.${p.state}`)}</Badge>
                    {p.reviewStatus !== 'not_required' && (
                      <Badge tone={reviewTone[p.reviewStatus]}>
                        {t(`admin.pages.reviews.${p.reviewStatus}`)}
                      </Badge>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Section>
          {locale && (
            <>
              <CategoriesSection storeId={storeId} locale={locale} locales={locales} />
              <CreatePostDialog
                open={creating}
                onOpenChange={setCreating}
                storeId={storeId}
                locale={locale}
              />
            </>
          )}
        </>
      )}
    </PageFrame>
  );
}

function CreatePostDialog({
  open,
  onOpenChange,
  storeId,
  locale,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  storeId: string;
  locale: string;
}) {
  const t = useT();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  useEffect(() => {
    if (open) setTitle('');
  }, [open]);
  const create = useApiMutation(
    () => api.post<{ id: string }>('/v1/admin/posts', { storeId, title, locale }),
    {
      invalidate: [['posts', storeId]],
      silentFieldErrors: true,
      onSuccess: (r) => {
        onOpenChange(false);
        void navigate({ to: '/blog/$id', params: { id: r.id } });
      },
    },
  );
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('admin.blog.create')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            disabled={!title.trim() || create.isPending}
            onClick={() => create.mutate(undefined)}
          >
            {t('admin.pages.createConfirm')}
          </Button>
        </>
      }
    >
      <Field
        label={t('admin.blog.title')}
        help={<HelpTip k="blog.title" />}
        error={fieldError(create.error, 'title')}
      >
        {(p) => <Input {...p} value={title} maxLength={160} onChange={(e) => setTitle(e.target.value)} />}
      </Field>
    </Dialog>
  );
}

/**
 * Blog kategorileri: ekleme ve çeviri (düzenleme yetkisi), ad/adres değişikliği ve silme (yayınlama
 * yetkisi). Kategoriler seçili dilde listelenir.
 */
function CategoriesSection({
  storeId,
  locale,
  locales,
}: {
  storeId: string;
  locale: string;
  locales: string[];
}) {
  const t = useT();
  const { can } = usePermissions();
  const canPublish = can('content.publish');
  const canWrite = can('content.write');
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<PostCategoryList[number] | null>(null);
  const cats = useQuery({
    queryKey: ['post-categories', storeId, locale],
    queryFn: () => api.get<PostCategoryList>(`/v1/admin/post-categories?storeId=${storeId}&locale=${locale}`),
    enabled: !!storeId,
  });
  const add = useApiMutation(() => api.post('/v1/admin/post-categories', { storeId, name, locale }), {
    invalidate: [['post-categories', storeId]],
    success: t('admin.blog.categorySaved'),
    onSuccess: () => setName(''),
  });
  const remove = useApiMutation((id: string) => api.del(`/v1/admin/post-categories/${id}`), {
    invalidate: [
      ['post-categories', storeId],
      ['posts', storeId],
    ],
    success: t('common.deleted'),
  });
  return (
    <Section title={t('admin.blog.categories')} description={t('admin.blog.categoriesLead')}>
      {cats.data && cats.data.length > 0 && (
        <ul className="mb-4 divide-y divide-line rounded-md border border-line bg-surface">
          {cats.data.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-sm">
              <span className="font-medium">{c.name}</span>
              <span className="text-ink-3">/blog/kategori/{c.slug}</span>
              <span className="text-ink-3">{t('admin.blog.postCount', { count: Number(c.posts) })}</span>
              {c.translations.length > 0 && (
                <span className="text-ink-3">
                  {c.translations.map((x) => `${localeName(x.locale)}: ${x.name}`).join(' · ')}
                </span>
              )}
              {canWrite && (
                <CategoryTranslate category={c} storeId={storeId} locale={locale} locales={locales} />
              )}
              {canPublish && (
                <span className="ms-auto flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(c)}>
                    {t('common.edit')}
                  </Button>
                  <ConfirmButton
                    variant="ghost"
                    message={t('admin.blog.categoryDeleteConfirm', { name: c.name })}
                    onConfirm={() => remove.mutateAsync(c.id)}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                    <span className="sr-only">{t('admin.blog.categoryDelete', { name: c.name })}</span>
                  </ConfirmButton>
                  <HelpTip k="blog.category_edit" />
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      {can('content.write') && (
        <div className="flex flex-wrap items-end gap-2">
          <Field
            label={t('admin.blog.categoryName')}
            help={<HelpTip k="blog.category_add" />}
            error={fieldError(add.error, 'name') ?? fieldError(add.error, 'slug')}
            className="w-72"
          >
            {(p) => <Input {...p} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Button disabled={!name.trim() || add.isPending} onClick={() => add.mutate(undefined)}>
            <Plus className="h-4 w-4" aria-hidden />
            {t('admin.blog.categoryAdd')}
          </Button>
        </div>
      )}
      <CategoryDialog category={editing} storeId={storeId} onClose={() => setEditing(null)} />
    </Section>
  );
}

/** Kategoriye çeviri: ad hedef dilde girilir (kategoriler taslaksızdır, vitrine hemen yansır). */
function CategoryTranslate({
  category,
  storeId,
  locale,
  locales,
}: {
  category: PostCategoryList[number];
  storeId: string;
  locale: string;
  locales: string[];
}) {
  const t = useT();
  const [name, setName] = useState('');
  const translate = useApiMutation(
    (target: string) =>
      api.post(`/v1/admin/post-categories/${category.id}/translations`, { locale: target, name }),
    {
      invalidate: [['post-categories', storeId]],
      success: t('admin.translations.created'),
      silentFieldErrors: true,
      onSuccess: () => setName(''),
    },
  );
  return (
    <TranslateButton
      locales={locales}
      current={locale}
      existing={category.translations.map((x) => x.locale)}
      pending={translate.isPending}
      error={fieldError(translate.error, 'locale')}
      canSubmit={!!name.trim()}
      onCreate={(l) => translate.mutate(l)}
    >
      {(target) => (
        <Field
          label={t('admin.translations.categoryName', { lang: localeName(target) })}
          help={<HelpTip k="blog.category_translation_name" />}
          error={fieldError(translate.error, 'name') ?? fieldError(translate.error, 'slug')}
        >
          {(p) => <Input {...p} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />}
        </Field>
      )}
    </TranslateButton>
  );
}

function CategoryDialog({
  category,
  storeId,
  onClose,
}: {
  category: PostCategoryList[number] | null;
  storeId: string;
  onClose: () => void;
}) {
  const t = useT();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  useEffect(() => {
    if (category) {
      setName(category.name);
      setSlug(category.slug);
      setDescription(category.description ?? '');
    }
  }, [category]);
  const save = useApiMutation(
    () =>
      api.put(`/v1/admin/post-categories/${category!.id}`, {
        name,
        slug,
        description: description || null,
        version: category!.version,
      }),
    {
      invalidate: [
        ['post-categories', storeId],
        ['posts', storeId],
      ],
      success: t('admin.blog.categorySaved'),
      silentFieldErrors: true,
      onSuccess: onClose,
    },
  );
  return (
    <Dialog
      open={!!category}
      onOpenChange={(o) => !o && onClose()}
      title={t('admin.blog.categoryEdit')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate(undefined)}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field
          label={t('admin.blog.categoryName')}
          help={<HelpTip k="blog.category_name" />}
          error={fieldError(save.error, 'name')}
        >
          {(p) => <Input {...p} value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <Field
          label={t('admin.pages.slug')}
          help={<HelpTip k="blog.category_slug" />}
          description={t('admin.blog.categorySlugLead')}
          error={fieldError(save.error, 'slug')}
        >
          {(p) => (
            <Input
              {...p}
              value={slug}
              maxLength={80}
              spellCheck={false}
              onChange={(e) => setSlug(e.target.value.toLowerCase())}
            />
          )}
        </Field>
        <Field
          label={t('admin.blog.categoryDescription')}
          optionalLabel={t('common.optional')}
          help={<HelpTip k="blog.category_description" />}
        >
          {(p) => (
            <Textarea
              {...p}
              value={description}
              maxLength={300}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------- Düzenleyici

interface Draft {
  title: string;
  slug: string;
  excerpt: string;
  body: unknown;
  coverFileId: string | null;
  coverAlt: string;
  categoryId: string;
  tags: string;
  references: Array<{ title: string; url: string }>;
  relatedProductIds: string[];
  authorName: string;
  authorTitle: string;
  seoTitle: string;
  seoDescription: string;
  healthContent: boolean;
}

function draftOf(p: PostDetail): Draft {
  return {
    title: p.title,
    slug: p.slug,
    excerpt: p.excerpt ?? '',
    body: p.body,
    coverFileId: p.coverFileId,
    coverAlt: p.coverAlt ?? '',
    categoryId: p.categoryId ?? '',
    tags: (p.tags ?? []).join(', '),
    references: (p.references ?? []).map((r) => ({ title: r.title, url: r.url ?? '' })),
    relatedProductIds: p.relatedProductIds ?? [],
    authorName: p.authorName ?? '',
    authorTitle: p.authorTitle ?? '',
    seoTitle: p.seoTitle ?? '',
    seoDescription: p.seoDescription ?? '',
    healthContent: p.healthContent,
  };
}

const stable = (v: unknown) =>
  JSON.stringify(v, (_k, x: unknown) =>
    x && typeof x === 'object' && !Array.isArray(x)
      ? Object.fromEntries(
          Object.entries(x as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b)),
        )
      : x,
  );

export function PostEditorPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const { id } = useParams({ from: '/app/blog/$id' });
  const navigate = useNavigate();
  const editorId = useId();
  const { can } = usePermissions();
  const canWrite = can('content.write');
  const canPublish = can('content.publish');
  const q = useQuery({ queryKey: ['post', id], queryFn: () => api.get<PostDetail>(`/v1/admin/posts/${id}`) });
  const post = q.data;
  const cats = useQuery({
    queryKey: ['post-categories', post?.storeId, post?.locale],
    queryFn: () =>
      api.get<PostCategoryList>(`/v1/admin/post-categories?storeId=${post!.storeId}&locale=${post!.locale}`),
    enabled: !!post,
  });
  const [draft, setDraft] = useState<Draft | null>(null);
  const [publishing, setPublishing] = useState(false);
  useEffect(() => {
    if (q.data) setDraft(draftOf(q.data));
  }, [q.data]);
  const dirty = !!post && !!draft && stable(draft) !== stable(draftOf(post));
  const body = () => ({
    title: draft!.title,
    slug: draft!.slug,
    excerpt: draft!.excerpt || null,
    body: draft!.body,
    coverFileId: draft!.coverFileId,
    coverAlt: draft!.coverAlt || null,
    categoryId: draft!.categoryId || null,
    tags: draft!.tags
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean),
    references: draft!.references
      .filter((r) => r.title.trim() || r.url.trim())
      .map((r) => ({ title: r.title, url: r.url || null })),
    relatedProductIds: draft!.relatedProductIds,
    authorName: draft!.authorName || null,
    authorTitle: draft!.authorTitle || null,
    seoTitle: draft!.seoTitle || null,
    seoDescription: draft!.seoDescription || null,
    healthContent: draft!.healthContent,
    version: post!.version,
  });
  const save = useApiMutation(() => api.put<PostDetail>(`/v1/admin/posts/${id}`, body()), {
    invalidate: [['post', id], ['posts']],
    success: t('admin.pages.saved'),
  });
  const ensureSaved = async (): Promise<number> => {
    if (!dirty) return post!.version;
    return (await api.put<PostDetail>(`/v1/admin/posts/${id}`, body())).version;
  };
  const preview = useApiMutation(
    async () => {
      await ensureSaved();
      return api.post<PreviewLink>(`/v1/admin/posts/${id}/preview-link`);
    },
    {
      invalidate: [['post', id]],
      onSuccess: (r) => {
        if (!openInNewTab(r.url)) toast.message(t('admin.design.previewBlocked'), { description: r.url });
      },
    },
  );
  const publish = useApiMutation(
    async (v: { publishAt: string; note: string }) => {
      const version = await ensureSaved();
      return api.post<PostDetail>(`/v1/admin/posts/${id}/publish`, {
        publishAt: v.publishAt ? new Date(v.publishAt).toISOString() : null,
        note: v.note || null,
        version,
      });
    },
    {
      invalidate: [['post', id], ['posts']],
      success: t('admin.blog.publishedToast'),
      onSuccess: () => setPublishing(false),
    },
  );
  const unpublish = useApiMutation(
    () => api.post<PostDetail>(`/v1/admin/posts/${id}/unpublish`, { version: post!.version }),
    { invalidate: [['post', id], ['posts']], success: t('admin.blog.unpublishedToast') },
  );
  // Makaleler sayfasında öne çıkar (F7-36): yayın sürümünü değiştirmez.
  const feature = useApiMutation(
    (featured: boolean) => api.post<PostDetail>(`/v1/admin/posts/${id}/feature`, { featured }),
    {
      invalidate: [['post', id], ['posts']],
      success: (r) => (r.featuredAt ? t('admin.blog.featuredToast') : t('admin.blog.unfeaturedToast')),
    },
  );
  const review = useApiMutation(
    (v: { decision: 'approved' | 'rejected'; note: string }) =>
      api.post<PostDetail>(`/v1/admin/posts/${id}/review`, {
        decision: v.decision,
        note: v.note || null,
        contentHash: post!.contentHash,
        version: post!.version,
      }),
    { invalidate: [['post', id], ['posts']], success: t('admin.pages.reviewedToast') },
  );
  const remove = useApiMutation(() => api.del(`/v1/admin/posts/${id}`), {
    invalidate: [['posts']],
    success: t('common.deleted'),
    onSuccess: () => void navigate({ to: '/blog' }),
  });
  const translate = useApiMutation(
    (locale: string) => api.post<PostDetail>(`/v1/admin/posts/${id}/translations`, { locale }),
    {
      invalidate: [['posts'], ['post', id]],
      success: t('admin.translations.created'),
      silentFieldErrors: true,
      onSuccess: (r) => void navigate({ to: '/blog/$id', params: { id: r.id } }),
    },
  );
  if (!post || !draft) return <Spinner label={t('common.loading')} />;
  const set = (patch: Partial<Draft>) => setDraft((d) => (d ? { ...d, ...patch } : d));
  const err = save.error ?? publish.error;
  const fe = (f: string) => fieldError(err, f);
  return (
    <PageFrame
      helpKey="page.post_editor"
      title={post.title}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          {canWrite && (
            <>
              <Button onClick={() => preview.mutate(undefined)} disabled={preview.isPending}>
                <ExternalLink className="h-4 w-4" aria-hidden />
                {t('admin.pages.preview')}
              </Button>
              <HelpTip k="blog.preview" />
              <Button onClick={() => save.mutate(undefined)} disabled={!dirty || save.isPending}>
                {t('admin.pages.save')}
              </Button>
              <HelpTip k="blog.save" />
            </>
          )}
          {canPublish && (
            <>
              <Button onClick={() => feature.mutate(!post.featuredAt)} disabled={feature.isPending}>
                {post.featuredAt ? t('admin.blog.unfeature') : t('admin.blog.feature')}
              </Button>
              <HelpTip k="blog.feature" />
              <Button variant="primary" onClick={() => setPublishing(true)}>
                {t('admin.pages.publish')}
              </Button>
              <HelpTip k="blog.publish" />
            </>
          )}
        </div>
      }
    >
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Link to="/blog" className="text-pine hover:underline">
          {t('admin.blog.back')}
        </Link>
        <Badge tone={stateTone[post.state]}>{t(`admin.blog.states.${post.state}`)}</Badge>
        {post.publishedVersion && (
          <Badge tone="neutral">{t('admin.pages.liveVersion', { version: post.publishedVersion })}</Badge>
        )}
        {(dirty || post.hasUnpublishedChanges) && <Badge tone="amber">{t('admin.pages.unpublished')}</Badge>}
        {post.featuredAt && <Badge tone="pine">{t('admin.blog.featuredBadge')}</Badge>}
        {post.publishAt && (
          <span className="text-ink-2">
            {t('admin.pages.publishAtInfo', { date: formatDate(post.publishAt, true) })}
          </span>
        )}
        <span className="text-ink-2">{t('admin.blog.readingTime', { n: post.readingMinutes })}</span>
      </div>
      <EditorLanguages
        storeId={post.storeId}
        locale={post.locale}
        translations={post.translations}
        to="/blog/$id"
        stateLabel={(s) => t(`admin.blog.states.${s}`)}
        canTranslate={canWrite}
        pending={translate.isPending}
        error={fieldError(translate.error, 'locale') ?? fieldError(translate.error, 'slug')}
        onCreate={(l) => translate.mutate(l)}
      />
      {canWrite && post.translations.length > 0 && (
        <PostTranslateDraft
          postId={post.id}
          sources={post.translations}
          onApply={(r) =>
            set({
              title: r.title,
              excerpt: r.excerpt ?? '',
              body: r.body,
              seoTitle: r.seoTitle ?? '',
              seoDescription: r.seoDescription ?? '',
            })
          }
        />
      )}

      <fieldset disabled={!canWrite} className="space-y-8">
        <Section title={t('admin.blog.article')} className="border-t-0 pt-0">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label={t('admin.blog.title')} help={<HelpTip k="blog.title" />} error={fe('title')}>
              {(p) => (
                <Input
                  {...p}
                  value={draft.title}
                  maxLength={160}
                  onChange={(e) => set({ title: e.target.value })}
                />
              )}
            </Field>
            <Field
              label={t('admin.pages.slug')}
              help={<HelpTip k="blog.slug" />}
              description={t('admin.pages.slugLead', { path: `/blog/${draft.slug}` })}
              error={fe('slug')}
            >
              {(p) => (
                <Input
                  {...p}
                  value={draft.slug}
                  maxLength={80}
                  spellCheck={false}
                  disabled={!!post.publishedVersion && !canPublish}
                  onChange={(e) => set({ slug: e.target.value.toLowerCase() })}
                />
              )}
            </Field>
            <Field
              label={t('admin.blog.excerpt')}
              optionalLabel={t('common.optional')}
              help={<HelpTip k="blog.excerpt" />}
              description={t('admin.pages.chars', { count: draft.excerpt.length, max: 300 })}
              error={fe('excerpt')}
              className="md:col-span-2"
            >
              {(p) => (
                <Textarea
                  {...p}
                  value={draft.excerpt}
                  maxLength={300}
                  rows={2}
                  onChange={(e) => set({ excerpt: e.target.value })}
                />
              )}
            </Field>
            <Field
              label={t('admin.blog.category')}
              optionalLabel={t('common.optional')}
              help={<HelpTip k="blog.category" />}
              error={fe('categoryId')}
            >
              {(p) => (
                <Select {...p} value={draft.categoryId} onChange={(e) => set({ categoryId: e.target.value })}>
                  <option value="">{t('admin.blog.noCategory')}</option>
                  {(cats.data ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field
              label={t('admin.blog.tags')}
              optionalLabel={t('common.optional')}
              help={<HelpTip k="blog.tags" />}
              description={t('admin.blog.tagsLead')}
              error={fe('tags')}
            >
              {(p) => <Input {...p} value={draft.tags} onChange={(e) => set({ tags: e.target.value })} />}
            </Field>
            <Field
              label={t('admin.blog.authorName')}
              optionalLabel={t('common.optional')}
              help={<HelpTip k="blog.author" />}
            >
              {(p) => (
                <Input
                  {...p}
                  value={draft.authorName}
                  maxLength={80}
                  onChange={(e) => set({ authorName: e.target.value })}
                />
              )}
            </Field>
            <Field
              label={t('admin.blog.authorTitle')}
              optionalLabel={t('common.optional')}
              help={<HelpTip k="blog.author" />}
            >
              {(p) => (
                <Input
                  {...p}
                  value={draft.authorTitle}
                  maxLength={80}
                  placeholder={t('admin.blog.authorTitlePlaceholder')}
                  onChange={(e) => set({ authorTitle: e.target.value })}
                />
              )}
            </Field>
          </div>
          <div className="mt-4 grid gap-2">
            <p className="text-sm font-medium">
              {t('admin.blog.cover')} <span className="font-normal text-ink-3">({t('common.optional')})</span>
            </p>
            <ImageUpload
              wide
              value={draft.coverFileId}
              label={draft.coverFileId ? t('admin.blog.replaceCover') : t('admin.blog.uploadCover')}
              removable
              help={<HelpTip k="blog.cover" />}
              onChange={(coverFileId) => set({ coverFileId })}
            />
            {fe('coverFileId') && (
              <p role="alert" className="text-sm font-medium text-rose">
                {fe('coverFileId')}
              </p>
            )}
            {draft.coverFileId && (
              <Field
                label={t('admin.pages.imageAlt')}
                help={<HelpTip k="pages.image_alt" />}
                error={fe('coverAlt')}
                required
                className="max-w-xl"
              >
                {(p) => (
                  <Input
                    {...p}
                    value={draft.coverAlt}
                    maxLength={300}
                    onChange={(e) => set({ coverAlt: e.target.value })}
                  />
                )}
              </Field>
            )}
          </div>
          <div className="mt-6">
            <RichTextEditor
              id={editorId}
              label={t('admin.blog.body')}
              value={draft.body}
              help={<HelpTip k="pages.rich_text" />}
              onChange={(b) => set({ body: b })}
            />
            {fe('body') && (
              <p role="alert" className="mt-1 text-sm font-medium text-rose">
                {fe('body')}
              </p>
            )}
          </div>
          <div className="mt-4 flex items-center gap-3">
            <Switch
              id="post-health"
              checked={draft.healthContent}
              onCheckedChange={(v) => set({ healthContent: v })}
              label={t('admin.pages.healthContent')}
            />
            <label htmlFor="post-health" className="text-sm font-medium">
              {t('admin.pages.healthContent')}
            </label>
            <HelpTip k="blog.health_content" />
          </div>
        </Section>

        <Section
          title={t('admin.blog.references')}
          description={t('admin.blog.referencesLead')}
          action={<HelpTip k="blog.references" />}
        >
          <ol className="grid gap-3">
            {draft.references.map((r, i) => (
              <li
                key={i}
                className="grid gap-2 rounded-sm border border-line p-3 md:grid-cols-[1fr_1fr_auto] md:items-end"
              >
                <Field
                  label={t('admin.blog.referenceTitle', { n: i + 1 })}
                  error={fe(`references.${i}.title`)}
                >
                  {(p) => (
                    <Input
                      {...p}
                      value={r.title}
                      maxLength={300}
                      onChange={(e) =>
                        set({
                          references: draft.references.map((x, j) =>
                            j === i ? { ...x, title: e.target.value } : x,
                          ),
                        })
                      }
                    />
                  )}
                </Field>
                <Field
                  label={t('admin.blog.referenceUrl', { n: i + 1 })}
                  optionalLabel={t('common.optional')}
                  error={fe(`references.${i}.url`)}
                >
                  {(p) => (
                    <Input
                      {...p}
                      value={r.url}
                      spellCheck={false}
                      placeholder="https://"
                      onChange={(e) =>
                        set({
                          references: draft.references.map((x, j) =>
                            j === i ? { ...x, url: e.target.value } : x,
                          ),
                        })
                      }
                    />
                  )}
                </Field>
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={t('admin.blog.referenceRemove', { n: i + 1 })}
                  onClick={() => set({ references: draft.references.filter((_, j) => j !== i) })}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              </li>
            ))}
          </ol>
          {draft.references.length < 30 && (
            <Button
              size="sm"
              className="mt-3"
              onClick={() => set({ references: [...draft.references, { title: '', url: '' }] })}
            >
              <Plus className="h-4 w-4" aria-hidden />
              {t('admin.blog.referenceAdd')}
            </Button>
          )}
        </Section>

        <Section title={t('admin.blog.related')} description={t('admin.blog.relatedLead')}>
          <ProductMultiPicker
            value={draft.relatedProductIds}
            label={t('admin.blog.relatedSearch')}
            help={<HelpTip k="blog.related" />}
            max={8}
            error={fe('relatedProductIds')}
            onChange={(relatedProductIds) => set({ relatedProductIds })}
          />
        </Section>

        <Section title={t('admin.blog.seo')}>
          <div className="grid gap-4 md:grid-cols-2">
            <Field
              label={t('admin.pages.seoTitle')}
              help={<HelpTip k="pages.seo_title" />}
              optionalLabel={t('common.optional')}
              description={t('admin.pages.chars', { count: draft.seoTitle.length, max: 70 })}
            >
              {(p) => (
                <Input
                  {...p}
                  value={draft.seoTitle}
                  maxLength={70}
                  onChange={(e) => set({ seoTitle: e.target.value })}
                />
              )}
            </Field>
            <Field
              label={t('admin.pages.seoDescription')}
              help={<HelpTip k="pages.seo_description" />}
              optionalLabel={t('common.optional')}
              description={t('admin.pages.chars', { count: draft.seoDescription.length, max: 170 })}
            >
              {(p) => (
                <Textarea
                  {...p}
                  value={draft.seoDescription}
                  maxLength={170}
                  onChange={(e) => set({ seoDescription: e.target.value })}
                />
              )}
            </Field>
          </div>
        </Section>
      </fieldset>

      <ContentReviewPanel
        status={post.reviewStatus}
        note={post.reviewNote}
        findings={post.findings}
        required={post.requiresReview}
        dirty={dirty}
        canReview={can('content.expert_review')}
        busy={review.isPending}
        onDecide={(decision, note) => review.mutate({ decision, note })}
      />

      <Section title={t('admin.pages.versions')} description={t('admin.pages.versionsLead')}>
        {!post.versions.length ? (
          <p className="text-sm text-ink-2">{t('admin.blog.noVersions')}</p>
        ) : (
          <ul className="divide-y divide-line rounded-md border border-line bg-surface">
            {post.versions.map((v) => (
              <li key={v.version} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 text-sm">
                <span className="font-medium">{t('admin.design.versionNo', { version: v.version })}</span>
                <span className="text-ink-2">
                  {formatDate(v.publishedAt, true)}
                  {v.publishedBy ? ` · ${v.publishedBy}` : ''}
                </span>
                {v.current && <Badge tone="moss">{t('admin.design.current')}</Badge>}
                {v.note && <span className="text-ink-2">{v.note}</span>}
              </li>
            ))}
          </ul>
        )}
        {canPublish && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {post.status === 'published' && (
              <>
                <ConfirmButton
                  variant="secondary"
                  message={t('admin.blog.unpublishConfirm')}
                  onConfirm={() => unpublish.mutateAsync(undefined)}
                >
                  {t('admin.pages.unpublish')}
                </ConfirmButton>
                <HelpTip k="blog.unpublish" />
              </>
            )}
            {!post.publishedVersion && (
              <>
                <ConfirmButton
                  message={t('admin.blog.deleteConfirm')}
                  onConfirm={() => remove.mutateAsync(undefined)}
                >
                  {t('common.delete')}
                </ConfirmButton>
                <HelpTip k="blog.delete" />
              </>
            )}
          </div>
        )}
      </Section>

      <PublishPostDialog
        open={publishing}
        onOpenChange={setPublishing}
        busy={publish.isPending}
        error={publish.error}
        dirty={dirty}
        onPublish={(v) => publish.mutate(v)}
      />
    </PageFrame>
  );
}

function PublishPostDialog({
  open,
  onOpenChange,
  busy,
  error,
  dirty,
  onPublish,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  busy: boolean;
  error: unknown;
  dirty: boolean;
  onPublish: (v: { publishAt: string; note: string }) => void;
}) {
  const t = useT();
  const [publishAt, setPublishAt] = useState('');
  const [note, setNote] = useState('');
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('admin.blog.publishTitle')}
      description={t('admin.blog.publishLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            disabled={busy}
            onClick={() => onPublish({ publishAt, note: note.trim() })}
          >
            {t('admin.pages.publishConfirm')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {dirty && <p className="text-sm text-ink-2">{t('admin.design.publishSavesFirst')}</p>}
        {error instanceof ApiError && (
          <p role="alert" className="text-sm font-medium text-rose">
            {error.errors[0]?.message ?? error.title}
          </p>
        )}
        <Field
          label={t('admin.pages.publishAt')}
          optionalLabel={t('common.optional')}
          help={<HelpTip k="blog.schedule" />}
        >
          {(p) => (
            <Input
              {...p}
              type="datetime-local"
              value={publishAt}
              onChange={(e) => setPublishAt(e.target.value)}
            />
          )}
        </Field>
        <Field
          label={t('admin.design.note')}
          optionalLabel={t('common.optional')}
          help={<HelpTip k="design.note" />}
        >
          {(p) => <Textarea {...p} value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} />}
        </Field>
      </div>
    </Dialog>
  );
}
